import React, { useRef } from "react";
import { createContext, useState } from "react";
import { Storyline } from "../interfaces/Storyline";
import { useContext } from "react";
import { Metadata } from "../utils/video";
import { useEffect } from "react";
import ConfirmDialog, { formItem } from "../components/ConfirmDialog";
import { cancelVideoAnalysis, getVideoAnalysis, runFixShorten, runShorten, runVideoAnalysis } from "../utils/api";
import { deleteItem, getItem, saveItem } from "../db";
import { toast } from 'react-toastify';
import { clearScreeshotCache } from "../utils/screenshot";
import { TranscriptionChunk } from "../interfaces/TranscriptionChunk";
import AuthDialog from "../components/Auth/AuthDialog";
import { VideoFragment } from "../interfaces/VideoFragment";
import { generateAAF, generateEDL, generateJSON, generateJSX, generateXML } from "../utils/export";
import { SettingsDialog } from "../components/SettingsDialog";



interface AppContextType {
    storylines: Storyline[],
    //saveStorylines: React.Dispatch<React.SetStateAction<Storyline[]>>;
    saveStorylines: (updater: Storyline[] | ((prev: Storyline[]) => Storyline[])) => Promise<Storyline[]>;
    toggleStorylineVisibility: (s: Storyline) => void
    setAllStorylinesVisibility: (val: boolean) => void
    metadata: Metadata | undefined,
    setMetadata: (metadata: Metadata) => void;
    checkAnalysisStatus: () => void;
    videoFile: File | null;
    setVideoFile: (file: File | null) => void;
    transcriptionChunks: any[];
    fragments: VideoFragment[];
    updateFragments: () => void;
    //saveTranscriptionChunks: React.Dispatch<React.SetStateAction<TranscriptionChunk[]>>;
    saveTranscriptionChunks: (updater: TranscriptionChunk[] | ((prev: TranscriptionChunk[]) => TranscriptionChunk[])) => Promise<TranscriptionChunk[]>;
    confirm: (text: string, title?: string, form?: formItem[]) => Promise<{ confirmed: boolean, params?: any }>;
    analyze: (rerun?: string[], params?: any) => void;
    loadExistedAnalysis: (callback?: () => void) => void;
    dataLoading: boolean;
    setDataLoading: (val: boolean) => void; 
    dataLoadingDetails: {progress:number, progress_label:string};
    cancelAnalysis: (job_id?: string) => void;
    videoRef: React.RefObject<HTMLVideoElement>;
    seekTo: (sec: number, autoplay?: boolean) => void;
    analisysError: string | undefined;
    zoom: number;
    setZoom: (zoom: number) => void;
    speakerLabels: Record<string, string>;
    updateSpeakerLabel: (name: string, label: string) => void;
    normalizedCurrentTime: number;
    setNormalizedCurrentTime: (currentTime: number) => void;
    totalTime: number;
    setTotalTime: (totalTime: number) => void;
    isPlaying: boolean;
    shorten: (target_duration: number) => void;
    fixShorten: ({ story_ids, transcription_line_ids, target_duration}: { story_ids: number[], transcription_line_ids: number[],target_duration: number  }) => void,
    shortening: boolean;
    resetShortening: () => void;
    showAuthDialog: () => void;
    showSettingsDialog: () => void;
    isOnline: boolean;
    exportSequence: (format: string, metadata?:Metadata) => void;
    usedWorkflow: string;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [storylines, setStorylines] = useState<Storyline[]>([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [dataLoadingDetails, setDataLoadingDetails] = useState({progress:0, progress_label:''});
    const [shortening, setShortening] = useState(false);
    const [transcriptionChunks, _setTranscriptionChunks] = useState<TranscriptionChunk[]>([])
    const [fragments, setFragments] = useState<VideoFragment[]>([]);
    const [metadata, setMetadata] = useState<Metadata | undefined>(undefined)
    const [analisysError, setAnalisysError] = useState<string | undefined>();
    const [zoom, setZoom] = useState<number>(1);
    const [speakerLabels, setSpeakerLabels] = useState<Record<string, string>>({});
    const [normalizedCurrentTime, setNormalizedCurrentTime] = useState<number>(0);
    const [totalTime, setTotalTime] = useState<number>(0);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [confirmDialog, setConfirmDialog] = useState<{ text: string, form?: formItem[], title?: string, callback: (confirmed: boolean, params?: any) => void } | null>();
    const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
    const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [usedWorkflow, setUsedWorkflow] = useState<string>("");

    useEffect(() => {
        init();
        return () => {
            clearScreeshotCache();
        }
    }, [metadata])



    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
        };
    }, [videoRef.current]);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const init = async () => {
        //console.log("recalculating visibility",storylines,fragments);
        if (metadata?.hash) {
            let slabels = await getItem("speaker_labels", metadata.hash);
            if (slabels?.labels) setSpeakerLabels(slabels.labels);
        }
    }



    const setStoriesAndTranscriptionChunks = async (data: any) => {
        if (!data.stories?.length && !data.events?.length) return;
        if (!data.stories) data.stories = [];
        if (!data.stories.find((s: any) => s.id === 0)) data.stories.push({ "id": 0, "title": "Other" })
        console.log("setting data stories", data?.stories);
        const visibility = await getItem("storyline_visibility", metadata?.hash || '');
        console.log("storyline_vis", visibility);
        if (data?.stories?.length) setStorylines(data.stories.map((st:Storyline)=>({...st,enabled: true})));
        data.events?.sort((a: any, b: any) => a.timestamp.begin - b.timestamp.begin); //just in case
        let totalTime = 0
        _setTranscriptionChunks(data.events.map((ch: TranscriptionChunk, index: number) => {
            const _ch = enrichChunkWithCalculatedData(ch, index, data?.stories?.length || 0);
            //console.log("adding duration",_ch.duration)
            totalTime += _ch.duration ?? 0
            return _ch
        }));
        console.log("setting totalTime", totalTime);
        setTotalTime(totalTime);

    }

    const enrichChunkWithCalculatedData = (chunk: TranscriptionChunk, index: number, max: number) => {

        //console.log("rs-",chunk.related_stories)

        let related_stories = chunk.related_stories?.filter((st: any) => st.id <= max).map((st: any) => {
            return {
                ...st,
                max_value: (st.value?.backstory > (st.value?.story_development || st.value?.development )) ? { backstory: st.value?.backstory } : { story_development: (st.value?.story_development || st.value?.development ) }
            }
        });



        //make sure there is no non-existed storyline _ids!!


        if (!related_stories?.length) related_stories = [{ "id": 0 }];

        //console.log("rs",related_stories)


        const maxGroupedEmotion = Object.entries(chunk.emotions || {}).reduce<
            [string, number]
        >(([maxEmotionName, maxScore], [emotion, data]: any) => {
            return data.score >= maxScore
                ? [emotion, data.score]
                : [maxEmotionName, maxScore];
        }, ["", 0]);

        const maxEmotion = Object.entries(chunk.emotions || {})
            .flatMap(([_, data]: any) =>
                Object.entries(data.components || {}).map(
                    ([emotionName, value]) => [emotionName, value] as [string, number]
                )
            )
            .reduce<[string, number]>(
                (maxEntry, current) =>
                    current[1] >= maxEntry[1] ? current : maxEntry,
                ["", 0]
            );

        const maxEntertainmentValue = Object.entries(
            chunk.entertainment_value || {}
        ).reduce<[string, number]>(
            (max, current: any) => (current[1] >= max[1] ? current : max),
            ["", 0]
        );
        return {
            ...chunk,
            index,
            related_stories,
            duration: Math.max(0,(chunk.timestamp.end - chunk.timestamp.begin)),
            speaker: chunk.speaker,
            text: chunk.text,
            max_grouped_emotion: {
                [maxGroupedEmotion[0]]: maxGroupedEmotion[1],
            },
            max_emotion: { [maxEmotion[0]]: maxEmotion[1] },
            max_entertainment_value: {
                [maxEntertainmentValue[0]]: maxEntertainmentValue[1],
            },
            all_max: Math.max(
                maxGroupedEmotion[1],
                maxEmotion[1],
                maxEntertainmentValue[1]
            ),
        }
    }

    const loadExistedAnalysis = async (callback?: () => void) => {
        setStorylines([])
        console.log("setting data loading true")
        setDataLoading(true);
        _setTranscriptionChunks([]);
        //setFragments([]);
        if (metadata?.hash) {
            const data = await getItem("analysis", metadata?.hash || '');
            console.log("existed state?", data?.state)
            if (!data) {
                if (callback) callback(); //can not set data loadinf false before running callback - so, setDataLoading(false) should be set in callback
                else {
                    console.log("setting data loading false because data not existed")
                    setDataLoading(false);
                }
            } else switch (data.state) {
                case 'completed':
                case 'canceled':
                    console.log("existed analysis", data);
                    setStoriesAndTranscriptionChunks(data.result);
                    console.log("setting data loading false because state is canceled")
                    setUsedWorkflow(data.name);
                    setDataLoading(false);
                    break;
                case 'processing':
                case 'active':
                case 'waiting':
                case 'delayed':
                    if (data.stories?.length && data.events?.length) { //means it's waiting for shortening
                        setStoriesAndTranscriptionChunks(data.result);
                        setShortening(true);
                        console.log("setting data loading false because loaded")
                        setDataLoading(false);
                    } else
                        scheduleAnalysisStatusCheck(data.job_id);
                    break;
                case 'failed':
                    //TODO show error!
                    console.log("setting data loading false because failed")
                    setDataLoading(false);
                    setAnalisysError(data.error || "Video Analysis Failed")
                    break;
            }
        } else if (callback) {
            callback();
            console.log("setting data loading false because no metadata and callback")
            setDataLoading(false);
        }
    }


    const analyze = async (rerun?: string[], params?: any) => {
        if (!metadata?.hash) {
            console.log("No  hash!!", metadata);
            toast.error("No video hash found. Please upload a video first.");
            throw ("Error")
        }
        console.log("setting data loading true")
        setDataLoading(true);
        deleteItem("storyline_visibility", metadata?.hash || '');
        setStorylines([])
        _setTranscriptionChunks([]);
        //setFragments([]);
        setAnalisysError(undefined);
        let existed;
        let data;
        try {
            if (rerun?.length) {
                existed = await getItem("analysis", metadata?.hash || '');
                data = await runVideoAnalysis(
                    { 
                        job_id: existed.job_id, 
                        ...rerun.length >1 ? {
                            rerun
                        }: {
                            startWith:rerun[0], 
                        },
                        params 

                    }); //rerun analysis 
            } else {
                console.log("starting new analysis")
                //let upload_id = "azure_" + metadata?.hash + "_undefined";
                //let uploaded = await getItem("uploads", upload_id);
                let uploaded = await getItem("uploads", "aws_" + metadata?.hash);
                let url = uploaded?.url;
                if (!url) {
                    //check if metadata.height>512 and downgrade video before upload to save server processing time
                    data = await runVideoAnalysis({ file: videoFile, params });
                    if (data.url && metadata?.hash) {
                        await saveItem("uploads", { url: data.url, id: "aws_" + metadata.hash });
                    }
                    //or we can also provide metadata for a file & separated audio track - in this case there be a much less work for server
                } else {
                    data = await runVideoAnalysis({ url });
                }
            }
            if (data?.id) {
                saveItem("analysis", { ...data, job_id: data?.id, id: metadata?.hash, });
                scheduleAnalysisStatusCheck(data?.id, 3000);
            }
            //console.log("runVideoAnalysis", data)
        } catch (err: any) {
            if (existed) loadExistedAnalysis();//in case of error loading trying to show the state before
            toast.error(err.message || err.error || "Video analysys error")
            setAnalisysError(err.message || err.error || "Video Analysis Failed")
            console.log("setting data loading false")
            setDataLoading(false);
        }
    }

    const scheduleAnalysisStatusCheck = (job_id: string, timeout_ms: number = 10000) => {
        setTimeout(async () => {
            let job = await checkAnalysisStatus(job_id);
            if (['processing', 'active', 'waiting', 'delayed'].includes(job.state)) scheduleAnalysisStatusCheck(job_id)
        }, timeout_ms)
    }

    const checkAnalysisStatus = async (job_id: string = "") => {
        if (!job_id) {
            const existed = await getItem("analysis", metadata?.hash || '');
            job_id = existed.job_id;
        }
        const data = await getVideoAnalysis(job_id);
        setUsedWorkflow(data.name);
        if (data.state === 'completed' && (data.result?.stories?.length || data.result?.events?.length)) {
            setStoriesAndTranscriptionChunks(data.result);
            setDataLoadingDetails({progress:0,progress_label:''});
        } else if (data.state === 'failed') {
            setAnalisysError(data.error || "Video Analysis Failed")
            setDataLoadingDetails({progress:0,progress_label:''});
        } else {
            setDataLoadingDetails({progress:data.progress,progress_label:data.progress_label});
        }
        saveItem("analysis", { ...data, job_id: data?.id, id: metadata?.hash, });
        console.log("updated", data);
        if (['error', 'failed', 'completed', 'canceled'].includes(data.state)) {
            console.log("setting data loading false")
            setDataLoading(false);
        }
        return data;
    }


    const cancelAnalysis = async (job_id?: string) => {
        if (!job_id) {
            const existed = await getItem("analysis", metadata?.hash || '');
            job_id = existed?.job_id;
            if (!job_id) return;
        }
        try {
            await cancelVideoAnalysis(job_id);
            checkAnalysisStatus(job_id);
        } catch (err: any) {
            console.log(err);
            toast.error("Error canceling analysis")
            setAnalisysError("Video Analysis Failed")
        }
    }

    const seekTo = (sec: number, autoplay?: boolean) => {
        let el = videoRef?.current;
        if (el) el.currentTime = sec;
        if (autoplay && el?.paused) el.play();
        //console.log("seeked",sec)
    }




    const shorten = async (target_duration: number) => {
        const existed = await getItem("analysis", metadata?.hash || '');
        const required_story_ids = storylines.filter(st => st.enabled).map(st => st.id);
        setShortening(true);
        console.log("Start shortening", target_duration, required_story_ids)
        const data = await runShorten({ job_id: existed.job_id, target_duration, required_story_ids }).catch((err: any) => {
            toast.error(err.error || err.message || "Error");
            setShortening(false);
        });
        if (data?.id) {
            scheduleShorteningStatusCheck(data.id, 5000);
        } else {
            toast.error("Unexpected error");
            setShortening(false);
        }
    }


    const scheduleShorteningStatusCheck = (job_id: string, timeout_ms: number = 3000) => {
        setTimeout(async () => {
            let job = await checkShorteningStatus(job_id);
            if (['processing', 'active', 'waiting', 'delayed'].includes(job.state)) scheduleShorteningStatusCheck(job_id)
        }, timeout_ms)
    }


    //need update
    const checkShorteningStatus = async (job_id: string) => {
        const data = await getVideoAnalysis(job_id);
        console.log("Checking shortening status", data);
        if (data.state === 'completed' && data.result) {
            console.log("need updated shortened ", data.result);
            const { ids } = data.result;
            const selected_line_ids = ids ?? [];
            if (selected_line_ids.length) {
                const _chunks = transcriptionChunks.map((chunk: TranscriptionChunk) => ({ ...chunk, excluded: !ids.includes(chunk.id) }))
                await saveTranscriptionChunks(_chunks);
                await saveStorylines((prev: Storyline[]) => prev.map((st: Storyline) => ({
                    ...st,
                    shortened: _chunks.filter(ch => (ch.excluded && ch.related_stories?.find(rs => rs.id === st.id))).length > 0
                })));
            }
        } else if (data.state === 'failed') {
            toast.error(data.error || "Video Shortening Failed")
        }
        if (['error', 'failed', 'completed'].includes(data.state)) setShortening(false);
        return data;
    }

    const fixShorten = async ({ story_ids, transcription_line_ids , target_duration}: { story_ids: number[], transcription_line_ids: number[], target_duration: number }) => {
        const existed = await getItem("analysis", metadata?.hash || '');
        if (!existed) {
            toast.error("No analysis found to fix");
            return;
        }
        setShortening(true);
        await runFixShorten({ job_id: existed.job_id,  story_ids, transcription_line_ids, target_duration  }).then(data => {
            console.log("fix_shorten", data);
            if (data?.id) {
                scheduleFixStatusCheck(data.id, 5000); //TODO maje universal status check instead of different checks for different jobs
            } else {
                toast.error("Unexpected error");
                setShortening(false);
            }
        }).catch((err: any) => {
            toast.error(err.error || err.message || "Error");
            setShortening(false);
        });
    }


    const scheduleFixStatusCheck = (job_id: string, timeout_ms: number = 3000) => {
        setTimeout(async () => {
            let job = await checkFixStatus(job_id);
            if (['processing', 'active', 'waiting', 'delayed'].includes(job.state)) scheduleFixStatusCheck(job_id)
        }, timeout_ms)
    }

    const checkFixStatus = async (job_id: string) => {
        const data = await getVideoAnalysis(job_id);
        console.log("Checking fix status", data);
        if (data.state === 'completed' && data.result) {
            const { line_ids_to_add, line_ids_to_remove } = data.result;
            console.log("lines",)
            let delta: number = 0
            const _chunks = transcriptionChunks.map((chunk: TranscriptionChunk) => {
                if (line_ids_to_remove.includes(chunk.id)) {
                    //console.log("Excluding chunk",chunk.id)
                    delta -= chunk.duration ?? 0;
                    return {
                        ...chunk,
                        excluded: true
                    };
                } else if (line_ids_to_add.includes(chunk.id)) {
                    //console.log("Adding chunk",chunk.id)
                    delta += chunk.duration ?? 0;
                    return {
                        ...chunk,
                        excluded: false
                    };
                }
                return chunk;
            })
            console.log("total delta applied", delta);
            await saveTranscriptionChunks(_chunks);
            await saveStorylines((prev: Storyline[]) => prev.map((st: Storyline) => ({
                ...st,
                shortened: _chunks.filter(ch => (ch.excluded && ch.related_stories?.find(rs => rs.id === st.id))).length > 0
            })));
            console.log("applied new fix: ", data.result);
        } else if (data.state === 'failed') {
            toast.error(data.error || "Video Fix Failed")
        }
        if (['error', 'failed', 'completed'].includes(data.state)) setShortening(false);
        return data;
    }


    const resetShortening = async () => {
        await saveTranscriptionChunks((prev: TranscriptionChunk[]) => prev.map((t: TranscriptionChunk) => ({ ...t, excluded: false })));
        await saveStorylines((prev: Storyline[]) => prev.map((s: Storyline) => ({ ...s, shortened: false })));
    }

    const toggleStorylineVisibility = (s: Storyline) => {
        saveStorylines((prev: Storyline[]) => {
            const newvalue = prev.map((st: Storyline) => st.id === s.id ? { ...st, enabled: !st.enabled } : st);
            saveItem("storyline_visibility", {
                id: metadata?.hash,
                visibility: newvalue.reduce((acc: any, st: Storyline) => {
                    acc[st.id] = st.enabled;
                    return acc;
                }, {} as Record<string, boolean>)
            });
            //storyline_visibility
            return newvalue;

        })
    }

    const setAllStorylinesVisibility = (val: boolean) => {
        saveStorylines((prev: Storyline[]) => {
            const newvalue = prev.map((s: Storyline) => ({ ...s, enabled: val }));
            saveItem("storyline_visibility", {
                id: metadata?.hash,
                visibility: newvalue.reduce((acc: any, st: Storyline) => {
                    acc[st.id] = st.enabled;
                    return acc;
                }, {} as Record<string, boolean>)
            });
            return newvalue;
        });
    }


    /*const getFragments = () => {
        return fragments
    }*/

    const confirm = (text: string, title?: string, form?: formItem[]): Promise<{ confirmed: boolean, params?: any }> => {
        return new Promise<{ confirmed: boolean, params?: any }>((resolve) => {
            setConfirmDialog({ text, title, form, callback: (confirmed: boolean, params?: any) => resolve({ confirmed, params }) });
        })
    }


    const onConfirmDialogClose = (confirmed: boolean, params?: any) => {
        if (confirmDialog?.callback) {
            confirmDialog.callback(confirmed, params)
        }
        setConfirmDialog(null);
    }

    const updateSpeakerLabel = (name: string, label: string) => {
        const newSpeakerLabels = { ...speakerLabels, [name]: label };
        saveItem("speaker_labels", { id: metadata?.hash, labels: newSpeakerLabels });
        console.log("new speaker labels", newSpeakerLabels)
        setSpeakerLabels(newSpeakerLabels);
    }


    const saveTranscriptionChunks: (updater: TranscriptionChunk[] | ((prev: TranscriptionChunk[]) => TranscriptionChunk[])) => Promise<TranscriptionChunk[]> =
        (updater) => {
            return new Promise(async (resolve) => {
                const savedstate = await getItem("analysis", metadata?.hash || '');
                _setTranscriptionChunks((prev) => {
                    const newChunks = typeof updater === 'function' ? updater(prev) : updater;
                    //console.log("saving transcription chunks");
                    saveItem("analysis", { ...savedstate, result: { ...savedstate.result, events: newChunks } })
                        .then(() => resolve(newChunks));
                    return newChunks;
                });
            });
        };

    const saveStorylines: (updater: Storyline[] | ((prev: Storyline[]) => Storyline[])) => Promise<Storyline[]> =
        (updater) => {
            return new Promise(async (resolve) => {
                const savedstate = await getItem("analysis", metadata?.hash || '');
                setStorylines((prev) => {
                    const newStorylines = typeof updater === 'function' ? updater(prev) : updater;
                    //console.log("saving storylines");
                    saveItem("analysis", { ...savedstate, result: { ...savedstate.result, stories: newStorylines } })
                        .then(() => resolve(newStorylines));
                    return newStorylines;
                });
            });
        };

    const showAuthDialog = () => {
        setIsAuthDialogOpen(true);
    }

    const showSettingsDialog = () => {
        setIsSettingsDialogOpen(true);
    }

    const updateFragments = () => {
        let newFragments: VideoFragment[] = [];
        let currentFragment: VideoFragment | null = null;
        let totalVisibleTime = 0; // Initialize total visible time
        let skip_delta = 0;

        if (!transcriptionChunks.length) {
            currentFragment = {
                timestamp: { begin: 0, end: metadata?.duration || 0 },
                visible: true,
                //thumbnails_at: [0],
                skip_delta: 0,
                emotions: []
            };
            //totalVisibleTime = metadata?.duration || 0 ;// don't need this because later we are adding last fragment
        }

        const visible_storylines_ids = storylines.filter(s => s.enabled).map(s => s.id);

        //console.log("Using chunks",transcriptionChunks)
        transcriptionChunks.forEach((chunk: TranscriptionChunk, i: number) => {
            const visible = !(chunk.related_stories || [])?.every((st: any) => !visible_storylines_ids.includes(st.id)) && !chunk.excluded;
            if (chunk.timestamp?.begin === undefined || chunk.timestamp?.end === undefined) return; //skip broken lines
            if (currentFragment && currentFragment.visible === visible) {
                currentFragment.timestamp.end = transcriptionChunks[i + 1]?.timestamp?.begin || chunk.timestamp.end;
                // currentFragment.thumbnails_at.push(chunk.timestamp.begin);
                currentFragment.emotions.push({
                    timestamp: chunk.timestamp.begin - currentFragment.timestamp.begin,
                    emotions: chunk.emotions || {}
                });
            } else {
                if (currentFragment) {
                    newFragments.push(currentFragment);

                    // Add to totalVisibleTime if the fragment is visible
                    if (currentFragment.visible) {
                        totalVisibleTime += currentFragment.timestamp.end - currentFragment.timestamp.begin;
                    } else {
                        skip_delta += currentFragment.timestamp.end - currentFragment.timestamp.begin;
                    }
                }
                currentFragment = {
                    timestamp: { begin: chunk.timestamp.begin, end: transcriptionChunks[i + 1]?.timestamp?.begin || chunk.timestamp.end },
                    visible,
                    //thumbnails_at: [chunk.timestamp.begin],
                    skip_delta, //how many seconds innisible  before this fragment,
                    emotions: [{
                        timestamp: 0,
                        emotions: chunk.emotions || {}
                    }]
                };
            }
        });

        if (currentFragment) { // Handle the last fragment
            newFragments.push(currentFragment);
            if (!currentFragment.timestamp.end) currentFragment.timestamp.end = metadata?.duration || 0;
            // Add to totalVisibleTime if the fragment is visible
            if (currentFragment.visible) {
                totalVisibleTime += currentFragment.timestamp.end - currentFragment.timestamp.begin;
            }
        } else {
            currentFragment = {
                timestamp: { begin: 0, end: metadata?.duration || 0 },
                visible: true,
                //thumbnails_at: [0],
                skip_delta: 0,
                emotions: []
            };
        }

        setFragments(newFragments);
        setTotalTime(totalVisibleTime);
        console.log("fragments:", newFragments);
    };

    const exportSequence = async (format: string) => {
        if (!metadata) {
            console.log("no metadata???",metadata)
            toast.error("No video loaded");
            return;
        }
        const videopath=metadata?.path || metadata?.name || 'Unknown.mp4';
        let filename=metadata?.name?.slice(0, metadata.name.lastIndexOf(".")) || metadata?.name || 'Unknown';
        let mime="text/plain";
        let content: string | Blob ="";
        switch (format) {
            case 'EDL':
                content=generateEDL(videopath,fragments,metadata);
                filename+=".edl";
                break;
            case 'JSX':
                content=generateJSX(videopath,fragments,metadata);
                mime="application/javascript";
                filename+=".jsx";
                break;
            case 'PREAAFJSON':
                content=JSON.stringify(generateJSON(videopath, fragments, metadata), null, 4); 
                mime="application/json";
                filename+=".json";
                break;
            case 'AAF':
                const toastId = toast.loading('Building AAF file..');
                try {
                    content= await generateAAF(videopath,fragments,metadata)
                    filename+=".aaf";
                    mime="application/octet-stream";
                } catch (error) {
                    console.error("AAF generation error:", error);
                    toast.error('AAF generation error');
                    throw('AAF generation error')
                } finally {
                    toast.dismiss(toastId);
                }
                break;
            default:
                content=generateXML(videopath,fragments,metadata);
                mime="text/xml";
                filename+=".xml";
        }
        saveFile(filename, content, mime);
    }

    async function saveFile(filename: string , content: string | Blob, mime: string): Promise<void> {
        if ("showSaveFilePicker" in window) {
            try {
                // Открываем диалог выбора файла
                const handle = await (window as any).showSaveFilePicker({
                    suggestedName: filename,
                    types: [
                        {
                            description: "Custom File",
                            accept: { [mime]: [`.${filename.split('.').pop()}`] }
                        }
                    ]
                });
    
                // Записываем данные в файл
                const writable = await handle.createWritable();
                if (typeof content === 'string') {
                    await writable.write(new Blob([content], { type: mime }));
                } else {
                    await writable.write(content);
                }
                await writable.close();
                return;
            } catch (error) {
                console.warn("Ошибка при сохранении через File System API, fallback к Blob:", error);
            }
        }
    
        // Fallback через Blob и `<a>`
        const blob = new Blob([content], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }


    const value = {
        storylines,
        saveStorylines,
        toggleStorylineVisibility,
        setAllStorylinesVisibility,
        transcriptionChunks,
        saveTranscriptionChunks,
        fragments,
        updateFragments,
        metadata,
        setMetadata,
        checkAnalysisStatus,
        videoFile,
        setVideoFile,
        confirm,
        analyze,
        loadExistedAnalysis,
        dataLoading,
        dataLoadingDetails,
        setDataLoading,
        cancelAnalysis,
        videoRef,
        seekTo,
        analisysError,
        zoom,
        setZoom,
        speakerLabels,
        updateSpeakerLabel,
        normalizedCurrentTime,
        setNormalizedCurrentTime,
        isPlaying,
        totalTime,
        setTotalTime,
        shorten,
        fixShorten,
        resetShortening,
        shortening,
        showAuthDialog,
        showSettingsDialog,
        isOnline,
        exportSequence,
        usedWorkflow
    };

    return <AppContext.Provider value={value}>
        {children}
        <ConfirmDialog
            isOpen={!!confirmDialog}
            text={confirmDialog?.text || ''}
            title={confirmDialog?.title}
            form={confirmDialog?.form}
            onClose={onConfirmDialogClose}

        />
        <AuthDialog isOpen={isAuthDialogOpen} allowClose={true} onClose={() => setIsAuthDialogOpen(false)} />
        <SettingsDialog isOpen={isSettingsDialogOpen} setOpen={setIsSettingsDialogOpen} />
    </AppContext.Provider>;
}

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};

